import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Users,
  Building2,
  Activity,
  Shield,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  AlertTriangle,
  UserPlus,
  Filter as FilterIcon,
  Settings,
  CheckCircle,
  CreditCard,
  Mail
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Partner, PartnerAdmin, Filter as FilterType } from '../types';
import { PromoRequestsManager } from './PromoRequestsManager';
import { SubscribersManager } from './SubscribersManager';
import { ContactInquiriesManager } from './ContactInquiriesManager';
import { MarketingCampaignManager } from './MarketingCampaignManager';
import { GlobalAdminApprovals } from './GlobalAdminApprovals';
import { ActivityLogsManager } from './ActivityLogsManager';
import { WebVisitorsDashboard } from './WebVisitorsDashboard';
import { MultiSelectCountries } from './MultiSelectCountries';
import { getCountryNames } from '../data/countries';
import { getPartnerLogoUrl } from '../utils/pocketbaseHelpers';
import pb from '../lib/pocketbase';
import { getBillingReport, BillingReportPreset, BillingReportResult } from '../services/billingReportService';
import { debugConfigService, DebugConfig } from '../services/debugConfig';
import { DataDeletionManager } from './DataDeletionManager';
import { AdminEmailManager } from './AdminEmailManager';

// Column configuration interface
interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  required?: boolean;
  order: number;
}

type FilterStatus = 'active' | 'inactive';

const formatDateInput = (date: Date) => date.toISOString().split('T')[0];

const getPresetRange = (preset: BillingReportPreset) => {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(end);

  switch (preset) {
    case 'last_7_days':
      start.setDate(start.getDate() - 6);
      break;
    case 'last_30_days':
      start.setDate(start.getDate() - 29);
      break;
    case 'this_month':
      start.setDate(1);
      break;
    case 'previous_month': {
      // Move to first day of current month, then step back one day to land in previous month
      const firstOfCurrent = new Date(end.getFullYear(), end.getMonth(), 1);
      // Last day of previous month
      end.setTime(firstOfCurrent.getTime() - 1);
      // First day of previous month
      start.setFullYear(end.getFullYear(), end.getMonth(), 1);
      break;
    }
    default:
      break;
  }

  start.setHours(0, 0, 0, 0);
  return { start, end };
};

export const GlobalAdminDashboard: React.FC = () => {
  const { 
    user,
    partners, 
    partnerAdmins, 
    partnerCampaigns,
    marketingCampaigns,
    categories,
    filters,
    createPartner, 
    updatePartner, 
    deletePartner,
    createPartnerAdmin,
    updatePartnerAdmin,
    deletePartnerAdmin,
    createCategory,
    updateCategory,
    deleteCategory,
    createFilter,
    updateFilter,
    deleteFilter
  } = useAuth();

  const [activeTab, setActiveTab] = useState<'overview' | 'partners' | 'admins' | 'campaigns' | 'approvals' | 'promo-requests' | 'subscribers' | 'contact-inquiries' | 'billing' | 'settings'>('overview');
  const [activeSettingsTab, setActiveSettingsTab] = useState<'categories' | 'filters' | 'activity-logs' | 'debug' | 'data-deletion' | 'send-email' | 'web-visitors'>('categories');
  const [showCreatePartnerModal, setShowCreatePartnerModal] = useState(false);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [editingAdmin, setEditingAdmin] = useState<PartnerAdmin | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [editingFilter, setEditingFilter] = useState<FilterType | null>(null);
  const [filterForm, setFilterForm] = useState<{ name: string; description: string; status: FilterStatus }>({
    name: '',
    description: '',
    status: 'active'
  });

  const partnerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    partners.forEach(partner => {
      if (partner.id) {
        map.set(partner.id, partner.name || '');
      }
    });
    return map;
  }, [partners]);

  const resetFilterForm = () => {
    setFilterForm({ name: '', description: '', status: 'active' });
  };

  const handleSaveFilter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedName = filterForm.name.trim();
    if (!trimmedName) {
      alert('Filter name is required');
      return;
    }

    try {
      if (editingFilter) {
        await updateFilter(editingFilter.id, {
          name: trimmedName,
          description: filterForm.description.trim(),
          status: filterForm.status
        });
      } else {
        await createFilter({
          name: trimmedName,
          description: filterForm.description.trim(),
          status: filterForm.status
        });
      }
      setShowFilterModal(false);
      setEditingFilter(null);
      resetFilterForm();
    } catch (error) {
      console.error('Failed to save filter:', error);
      alert('Failed to save filter: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleDeleteFilter = async (filter: FilterType) => {
    if (window.confirm(`Delete filter "${filter.name}"?`)) {
      try {
        await deleteFilter(filter.id);
      } catch (error) {
        console.error('Failed to delete filter:', error);
        alert('Failed to delete filter: ' + (error instanceof Error ? error.message : 'Unknown error'));
      }
    }
  };
  const initialBillingRange = useMemo(() => getPresetRange('last_7_days'), []);
  const [billingPreset, setBillingPreset] = useState<BillingReportPreset>('last_7_days');
  const [billingStartDate, setBillingStartDate] = useState(formatDateInput(initialBillingRange.start));
  const [billingEndDate, setBillingEndDate] = useState(formatDateInput(initialBillingRange.end));
  const [billingSelectedPartner, setBillingSelectedPartner] = useState<string>('all');
  const [billingResult, setBillingResult] = useState<BillingReportResult | null>(null);
  // Aggregated result for all partners in the selected period, used for ranking (A–D)
  const [billingResultAllPartners, setBillingResultAllPartners] = useState<BillingReportResult | null>(null);
  // Sorting state for Partner Breakdown table
  type BillingSortColumn = 'partner' | 'platinum' | 'gold' | 'silver' | 'bronze' | 'magicLinkClicks' | 'promoRequests' | 'ranking';
  type SortDirection = 'asc' | 'desc';
  const [billingSortColumn, setBillingSortColumn] = useState<BillingSortColumn>('partner');
  const [billingSortDirection, setBillingSortDirection] = useState<SortDirection>('asc');
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const billingReportRef = useRef<HTMLDivElement | null>(null);
  
  // Debug configuration state
  const [debugConfig, setDebugConfig] = useState<DebugConfig | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);

  // Default column configuration
  const defaultColumnConfig: ColumnConfig[] = [
    { key: 'company', label: 'Company', visible: true, required: true, order: 0 },
    { key: 'category', label: 'Category', visible: true, order: 1 },
    { key: 'maxCampaigns', label: 'Max Campaigns', visible: true, order: 2 },
    { key: 'activeCampaigns', label: 'Active Campaigns', visible: true, order: 3 },
    { key: 'webDomain', label: 'Web Domain', visible: true, order: 4 },
    { key: 'privacyPolicy', label: 'Privacy Policy', visible: true, order: 5 },
    { key: 'region', label: 'Region', visible: true, order: 6 },
    { key: 'status', label: 'Status', visible: true, order: 7 },
    { key: 'created', label: 'Created', visible: true, order: 8 },
    { key: 'actions', label: 'Actions', visible: true, required: true, order: 9 }
  ];

  // Column configuration state with PocketBase persistence
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>(defaultColumnConfig);

  // Load column config from user preferences on mount
  useEffect(() => {
    const loadColumnPreferences = async () => {
      if (!user?.id) return;
      
      try {
        const savedConfig = await pb.collection('users').getOne(user.id);
        if (savedConfig.column_preferences) {
          const preferences = typeof savedConfig.column_preferences === 'string' 
            ? JSON.parse(savedConfig.column_preferences)
            : savedConfig.column_preferences;
          
          if (preferences.partnerColumns) {
            setColumnConfig(preferences.partnerColumns);
          }
        }
      } catch (error) {
        console.log('No saved column preferences found, using defaults');
      }
    };
    
    loadColumnPreferences();
  }, [user?.id]);

  // Save column config to PocketBase when it changes
  const saveColumnPreferences = async (newConfig: ColumnConfig[]) => {
    if (!user?.id) return;
    
    try {
      // Get current preferences
      const currentUser = await pb.collection('users').getOne(user.id);
      const currentPreferences = currentUser.column_preferences 
        ? (typeof currentUser.column_preferences === 'string' 
            ? JSON.parse(currentUser.column_preferences)
            : currentUser.column_preferences)
        : {};
      
      // Update with new partner columns config
      const updatedPreferences = {
        ...currentPreferences,
        partnerColumns: newConfig
      };
      
      // Save to database
      await pb.collection('users').update(user.id, {
        column_preferences: updatedPreferences
      });
      
      console.log('Column preferences saved successfully');
    } catch (error) {
      console.error('Failed to save column preferences:', error);
    }
  };

  // Handle click outside to close column config dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showColumnConfig && !target.closest('.column-config-dropdown') && !target.closest('.column-config-button')) {
        setShowColumnConfig(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnConfig]);

  const loadBillingReport = useCallback(async () => {
    try {
      setBillingLoading(true);
      setBillingError(null);

      const start = new Date(billingStartDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(billingEndDate);
      end.setHours(23, 59, 59, 999);

      const partnerId = billingSelectedPartner === 'all' ? undefined : billingSelectedPartner;

      // Always load aggregated metrics for all partners (used for ranking)
      const reportAll = await getBillingReport({
        startDate: start,
        endDate: end,
        metricMode: 'quotas'
      });
      setBillingResultAllPartners(reportAll);

      // If viewing "All partners", reuse the aggregated report to avoid a second query
      if (!partnerId) {
        setBillingResult(reportAll);
      } else {
        const reportFiltered = await getBillingReport({
          startDate: start,
          endDate: end,
          partnerId,
          metricMode: 'quotas'
        });
        setBillingResult(reportFiltered);
      }
    } catch (error: any) {
      console.error('Billing report load failed:', error);
      setBillingError(error?.message || 'Failed to load billing report');
    } finally {
      setBillingLoading(false);
    }
  }, [billingStartDate, billingEndDate, billingSelectedPartner]);

  const handleBillingSort = (column: BillingSortColumn) => {
    setBillingSortColumn((current) => {
      if (current === column) {
        // Toggle direction when clicking the same column
        setBillingSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        return current;
      }
      // New column defaults to ascending
      setBillingSortDirection('asc');
      return column;
    });
  };

  useEffect(() => {
    if (activeTab === 'billing') {
      loadBillingReport();
    }
  }, [activeTab, loadBillingReport]);

  // Load debug config when debug settings tab is active
  useEffect(() => {
    const loadDebugConfig = async () => {
      setDebugLoading(true);
      try {
        const config = await debugConfigService.getConfig();
        setDebugConfig(config);
      } catch (error) {
        console.error('Failed to load debug config:', error);
      } finally {
        setDebugLoading(false);
      }
    };
    if (activeSettingsTab === 'debug') {
      loadDebugConfig();
    }
  }, [activeSettingsTab]);

  const handlePresetChange = (preset: BillingReportPreset) => {
    setBillingPreset(preset);
    if (preset === 'custom') return;
    const range = getPresetRange(preset);
    setBillingStartDate(formatDateInput(range.start));
    setBillingEndDate(formatDateInput(range.end));
  };

  const handleExportPdf = () => {
    if (!billingResult || !billingReportRef.current) {
      setBillingError('Generate a report before exporting to PDF.');
      return;
    }

    const printContents = billingReportRef.current.innerHTML;
    const printWindow = window.open('', '', 'width=900,height=650');
    if (!printWindow) {
      setBillingError('Popup blocked. Allow popups to export PDF.');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Billing Report</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 24px; color: #111827; }
            h1 { font-size: 24px; margin-bottom: 8px; }
            .muted { color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th, td { border: 1px solid #e5e7eb; padding: 8px 12px; text-align: left; font-size: 13px; }
            th { background: #f3f4f6; text-transform: uppercase; font-weight: 600; }
            .cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-top: 16px; }
            .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; }
            .card-title { font-size: 12px; text-transform: uppercase; color: #6b7280; }
            .card-value { font-size: 24px; font-weight: 600; margin-top: 8px; }
          </style>
        </head>
        <body>
          <h1>Billing Report</h1>
          <p class="muted">${new Date(billingStartDate).toLocaleDateString()} - ${new Date(billingEndDate).toLocaleDateString()}</p>
          ${printContents}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Helper function to render table cell content based on column key
  const renderTableCell = (partner: Partner, columnKey: string) => {
    switch (columnKey) {
      case 'company':
        const logoUrl = getPartnerLogoUrl(partner);
        return (
          <td className="px-6 py-4">
            <div className="flex items-center">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={`${partner.name} logo`}
                  className="w-12 h-12 rounded-lg mr-3 object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <div className={`bg-blue-100 p-3 rounded-lg mr-3 ${logoUrl ? 'hidden' : ''}`}>
                <Building2 className="text-blue-600" size={24} />
              </div>
              <div className="text-sm font-medium text-gray-900">
                {partner.name}
              </div>
            </div>
          </td>
        );
      case 'category':
        return (
          <td className="px-6 py-4">
            <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getCategoryColor(partner.category || '')}`}>
              {partner.category || 'No Category'}
            </span>
          </td>
        );
      case 'maxCampaigns':
        return (
          <td className="px-6 py-4 text-sm text-gray-900">
            {partner.max_campaigns}
          </td>
        );
      case 'activeCampaigns':
        return (
          <td className="px-6 py-4 text-sm text-gray-900">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="font-medium">{getPartnerActiveCampaigns(partner.id)}</span>
                {getPartnerUnincludedCampaigns(partner.id) > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {getPartnerUnincludedCampaigns(partner.id)} not included
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500">of {getPartnerTotalCampaigns(partner.id)} total</span>
            </div>
          </td>
        );
      case 'webDomain':
        return (
          <td className="px-6 py-4 text-sm text-gray-900">
            <div className="flex items-center">
              <span className="text-blue-600 hover:text-blue-800 cursor-pointer">
                {partner.web_domain || 'TBU.COM'}
              </span>
            </div>
          </td>
        );
      case 'privacyPolicy':
        return (
          <td className="px-6 py-4 text-sm text-gray-900">
            <div className="flex items-center">
              {partner.privacy_policy_url ? (
                <a 
                  href={partner.privacy_policy_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
                >
                  View Policy
                </a>
              ) : (
                <span className="text-gray-400 text-xs">Not set</span>
              )}
            </div>
          </td>
        );
      case 'region':
        return (
          <td className="px-6 py-4 text-sm text-gray-900">
            <div className="flex flex-wrap gap-1">
              {partner.region && partner.region.length > 0 ? (
                partner.region.slice(0, 3).map(countryCode => (
                  <span 
                    key={countryCode}
                    className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded"
                    title={getCountryNames([countryCode])[0]}
                  >
                    {countryCode}
                  </span>
                ))
              ) : (
                <span className="text-gray-500 text-xs">Default</span>
              )}
              {partner.region && partner.region.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{partner.region.length - 3} more
                </span>
              )}
            </div>
          </td>
        );
      case 'status':
        return (
          <td className="px-6 py-4">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(partner.status || 'active')}`}>
              {partner.status || 'active'}
            </span>
          </td>
        );
      case 'created':
        return (
          <td className="px-6 py-4 text-sm text-gray-500">
            {partner.created_at 
              ? new Date(partner.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })
              : 'N/A'
            }
          </td>
        );
      case 'actions':
        return (
          <td className="px-6 py-4 text-sm">
            <div className="flex space-x-2">
              <button
                onClick={() => startEditPartner(partner)}
                className="text-blue-600 hover:text-blue-800 transition duration-200"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={() => deletePartner(partner.id)}
                className="text-red-600 hover:text-red-800 transition duration-200"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </td>
        );
      default:
        return <td className="px-6 py-4"></td>;
    }
  };

  const [partnerForm, setPartnerForm] = useState({
    name: '',
    category: '', // Keep for backward compatibility
    category_id: '', // New field for category ID
    max_campaigns: 10,
    status: 'active' as Partner['status'],
    logo_url: '',
    web_domain: '',
    privacy_policy_url: '',
    region: [] as string[],
    integration_settings: {
      email_notifications: false,
      api_access: false,
      webhook_access: false
    },
    priority_quotas: {
      platinum: 0,
      gold: 0,
      silver: 0,
      bronze: 0
    }
  });

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logoDragActive, setLogoDragActive] = useState(false);

  const [adminForm, setAdminForm] = useState({
    name: '',
    email: '',
    partner_id: '',
    status: 'active' as PartnerAdmin['status'],
    activation_method: 'password' as 'password' | 'email_link',
    initial_password: ''
  });

  // Remove local categories state - now using categories from AuthContext

  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    status: 'active' as 'active' | 'inactive'
  });

  // Error states
  const [partnerError, setPartnerError] = useState<string | null>(null);

  const resetPartnerForm = () => {
    setPartnerForm({
      name: '',
      category: '',
      category_id: '',
      max_campaigns: 10,
      status: 'active',
      logo_url: '',
      web_domain: '',
      privacy_policy_url: '',
      region: [],
      integration_settings: {
        email_notifications: false,
        api_access: false,
        webhook_access: false
      },
      priority_quotas: {
        platinum: 0,
        gold: 0,
        silver: 0,
        bronze: 0
      }
    });
    setLogoFile(null);
    setLogoPreview('');
  };

  const handleLogoFileChange = (file: File | null) => {
    if (!file) {
      setLogoFile(null);
      setLogoPreview('');
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Invalid file type. Please upload JPG, PNG, WebP, or SVG images only.');
      return;
    }

    // Validate file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      alert('File size too large. Please upload an image smaller than 2MB.');
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
      // Clear logo_url when file is uploaded
      setPartnerForm(prev => ({ ...prev, logo_url: '' }));
    };
    reader.readAsDataURL(file);
  };

  const handleLogoDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setLogoDragActive(true);
    } else if (e.type === 'dragleave') {
      setLogoDragActive(false);
    }
  };

  const handleLogoDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLogoDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleLogoFileChange(e.dataTransfer.files[0]);
    }
  };

  const resetAdminForm = () => {
    setAdminForm({
      name: '',
      email: '',
      partner_id: '',
      status: 'active',
      activation_method: 'password',
      initial_password: ''
    });
  };

  const handleCreatePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    setPartnerError(null); // Clear previous errors
    
    try {
      // Pass the logo file to createPartner (PocketBase will handle the upload)
      await createPartner(partnerForm, logoFile);
      setShowCreatePartnerModal(false);
      resetPartnerForm();
    } catch (error) {
      console.error('Failed to create partner:', error);
      setPartnerError(error instanceof Error ? error.message : 'Failed to create partner');
      // Keep modal open on error so user can try again
    }
  };

  const handleUpdatePartner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPartner) {
      try {
        // Pass the logo file to updatePartner (PocketBase will handle the upload)
        await updatePartner(editingPartner.id, partnerForm, logoFile);
        setEditingPartner(null);
        resetPartnerForm();
      } catch (error) {
        console.error('Failed to update partner:', error);
        alert('Failed to update partner: ' + error);
      }
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createPartnerAdmin({
        ...adminForm,
        first_login_completed: false,
        is_activated: adminForm.activation_method === 'password' // Auto-activate if using password method
      });
      console.log('Partner admin created, modal closing...');
      setShowCreateAdminModal(false);
      resetAdminForm();
      console.log('Partner admins in state:', partnerAdmins.length);
    } catch (error) {
      console.error('Error creating admin:', error);
      alert('Failed to create partner admin: ' + error);
    }
  };

  const handleUpdateAdmin = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingAdmin) {
      updatePartnerAdmin(editingAdmin.id, adminForm);
      setEditingAdmin(null);
      resetAdminForm();
    }
  };

  const startEditPartner = (partner: Partner) => {
    const editPartner = (partner: any) => {
      setPartnerForm({
        name: partner.name || '',
        category: partner.category || '',
        category_id: partner.category_id || '',
        max_campaigns: partner.max_campaigns || 10,
        status: partner.status || 'active',
        logo_url: partner.logo_url || '',
        web_domain: partner.web_domain || '',
        privacy_policy_url: partner.privacy_policy_url || '',
        region: partner.region || [],
        integration_settings: partner.integration_settings || {
          email_notifications: true,
          api_access: false,
          webhook_access: false
        },
        priority_quotas: partner.priority_quotas || {
          platinum: 0,
          gold: 0,
          silver: 0,
          bronze: 0
        }
      });
      
      // Set logo preview - use helper to get PocketBase file URL or logo_url
      const logoUrl = getPartnerLogoUrl(partner);
      if (logoUrl) {
        setLogoPreview(logoUrl);
      } else {
        setLogoPreview('');
      }
      setLogoFile(null); // Clear any file selection
      
      setEditingPartner(partner);
      setShowCreatePartnerModal(true);
    };
    editPartner(partner);
  };

  const startEditAdmin = (admin: PartnerAdmin) => {
    setEditingAdmin(admin);
    setAdminForm({
      name: admin.name,
      email: admin.email,
      partner_id: admin.partner_id,
      status: admin.status,
      activation_method: admin.activation_method || 'password',
      initial_password: admin.initial_password || ''
    });
  };

  const filteredPartners = partners.filter(partner => {
    const matchesSearch = partner.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    const matchesCategory = categoryFilter === 'all' || partner.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const filteredAdmins = partnerAdmins.filter(admin => {
    const query = searchTerm.toLowerCase();
    const nameMatch = admin.name?.toLowerCase().includes(query);
    const emailMatch = admin.email?.toLowerCase().includes(query);
    const partnerMatch = partnerNameMap.get(admin.partner_id || '')?.toLowerCase().includes(query);
    return Boolean(nameMatch || emailMatch || partnerMatch);
  });

  const getPartnerName = (partnerId: string) => {
    const partner = partners.find(p => p.id === partnerId);
    return partner?.name || 'Unknown Partner';
  };

  const getPartnerActiveCampaigns = (partnerId: string) => {
    return partnerCampaigns.filter(pc => pc.partner_id === partnerId && pc.status === 'active').length;
  };

  const getPartnerTotalCampaigns = (partnerId: string) => {
    return partnerCampaigns.filter(pc => pc.partner_id === partnerId).length;
  };

  // Check if a partner campaign is included in any global marketing campaign
  const isPartnerCampaignIncluded = (campaignId: string) => {
    return marketingCampaigns.some(mc => 
      mc.partner_campaign_ids && mc.partner_campaign_ids.includes(campaignId)
    );
  };

  // Get count of unincluded partner campaigns for a partner
  const getPartnerUnincludedCampaigns = (partnerId: string) => {
    return partnerCampaigns.filter(pc => 
      pc.partner_id === partnerId && 
      pc.status === 'active' && 
      !isPartnerCampaignIncluded(pc.id)
    ).length;
  };

  const getCategoryColor = (category: string) => {
    if (!category) return 'bg-gray-100 text-gray-800 border-gray-200';
    
    // Normalize category name to lowercase for consistent matching
    const normalizedCategory = category.toLowerCase().trim();
    
    const colors = {
      'marketplace': 'bg-blue-100 text-blue-800 border-blue-200',
      'finance and banking': 'bg-green-100 text-green-800 border-green-200',
      'bank': 'bg-green-100 text-green-800 border-green-200',
      'hotels': 'bg-purple-100 text-purple-800 border-purple-200',
      'hotel': 'bg-purple-100 text-purple-800 border-purple-200',
      'rertail': 'bg-orange-100 text-orange-800 border-orange-200',
      'retail': 'bg-orange-100 text-orange-800 border-orange-200',
      'restaurants': 'bg-red-100 text-red-800 border-red-200',
      'restaurant': 'bg-red-100 text-red-800 border-red-200',
      'delivery': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'real estate': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'healthcare': 'bg-pink-100 text-pink-800 border-pink-200',
      'technology': 'bg-violet-100 text-violet-800 border-violet-200',
      'education': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'entertainment': 'bg-rose-100 text-rose-800 border-rose-200'
    };
    
    // Check if we have a predefined color for this category
    const colorKey = normalizedCategory as keyof typeof colors;
    if (colors[colorKey]) {
      return colors[colorKey];
    }
    
    // Generate consistent color based on category name for dynamic categories
    let hash = 0;
    for (let i = 0; i < normalizedCategory.length; i++) {
      const char = normalizedCategory.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    const colorOptions = [
      'bg-cyan-100 text-cyan-800 border-cyan-200',
      'bg-teal-100 text-teal-800 border-teal-200',
      'bg-lime-100 text-lime-800 border-lime-200',
      'bg-amber-100 text-amber-800 border-amber-200',
      'bg-sky-100 text-sky-800 border-sky-200',
      'bg-slate-100 text-slate-800 border-slate-200'
    ];
    
    return colorOptions[Math.abs(hash) % colorOptions.length];
  };

  const getStatusColor = (status: string) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const MetricCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    description?: string;
  }> = ({ title, value, icon, color, description }) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition duration-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
          {description && (
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          )}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Global Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage partners, admins, and system overview</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex items-center justify-between px-4 sm:px-0">
              <div className="flex space-x-8 overflow-x-auto">
                {[
                  { id: 'overview', label: 'Overview', icon: Activity },
                  { id: 'partners', label: 'Partners', icon: Building2 },
                  { id: 'admins', label: 'Users', icon: Users },
                  { id: 'campaigns', label: 'Campaigns', icon: Shield },
                  { id: 'approvals', label: 'Approvals', icon: CheckCircle },
                  { id: 'promo-requests', label: 'Promo Requests', icon: UserPlus },
                  { id: 'subscribers', label: 'Subscribers', icon: Mail },
                  { id: 'contact-inquiries', label: 'Inquiries', icon: Eye },
                  { id: 'billing', label: 'Billing', icon: CreditCard }
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id as any)}
                    className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={20} className="mr-2" />
                    {label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setActiveTab('settings')}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ml-4 whitespace-nowrap ${
                  activeTab === 'settings'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                title="Settings"
              >
                <Settings size={20} />
              </button>
            </nav>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Total Partners"
                value={partners.length}
                icon={<Building2 className="text-white" size={24} />}
                color="bg-blue-500"
                description="Active partner companies"
              />
              <MetricCard
                title="Partner Admins"
                value={partnerAdmins.length}
                icon={<Users className="text-white" size={24} />}
                color="bg-green-500"
                description="Total admin accounts"
              />
              <MetricCard
                title="Active Partners"
                value={partners.filter(p => p.status === 'active').length}
                icon={<Activity className="text-white" size={24} />}
                color="bg-purple-500"
                description="Currently active"
              />
              <MetricCard
                title="Total Campaigns"
                value={partnerCampaigns.length}
                icon={<Shield className="text-white" size={24} />}
                color="bg-orange-500"
                description="Across all partners"
              />
              <MetricCard
                title="Unincluded Campaigns"
                value={partnerCampaigns.filter(pc => pc.status === 'active' && !isPartnerCampaignIncluded(pc.id)).length}
                icon={<AlertTriangle className="text-white" size={24} />}
                color="bg-yellow-500"
                description="Active campaigns not in global campaigns"
              />
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Recent Partners</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {partners.slice(0, 5).map((partner) => (
                    <div key={partner.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="bg-blue-100 p-2 rounded-lg">
                          <Building2 className="text-blue-600" size={20} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{partner.name}</p>
                          <p className="text-sm text-gray-500">
                            {partner.category} • {getPartnerTotalCampaigns(partner.id)} campaigns
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(partner.status || 'active')}`}>
                        {partner.status || 'active'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Filter Modal */}
        {showFilterModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingFilter ? 'Edit Filter' : 'Create New Filter'}
              </h3>
              <form onSubmit={handleSaveFilter} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter Name</label>
                  <input
                    type="text"
                    value={filterForm.name}
                    onChange={(e) => setFilterForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter filter name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={filterForm.description}
                    onChange={(e) => setFilterForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Short description"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={filterForm.status}
                    onChange={(e) => setFilterForm(prev => ({ ...prev, status: e.target.value as FilterStatus }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFilterModal(false);
                      setEditingFilter(null);
                      resetFilterForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition duration-200"
                  >
                    {editingFilter ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Partners Tab */}
        {activeTab === 'partners' && (
          <div className="space-y-6">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search partners..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="relative">
                  <FilterIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex space-x-3">
                {/* Column Configuration Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowColumnConfig(!showColumnConfig)}
                    className="column-config-button bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition duration-200 flex items-center"
                  >
                    <Settings size={20} className="mr-2" />
                    Columns
                  </button>
                  
                  {/* Column Configuration Dropdown */}
                  {showColumnConfig && (
                    <div 
                      className="column-config-dropdown absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-gray-900 mb-3">Show/Hide Columns</h3>
                        <div className="space-y-2">
                          {columnConfig.map((column) => {
                            const handleToggle = () => {
                              if (column.required) return;
                              
                              console.log('Toggling column:', column.key, 'from', column.visible, 'to', !column.visible);
                              
                              setColumnConfig(prev => {
                                const updated = prev.map(col => 
                                  col.key === column.key 
                                    ? { ...col, visible: !col.visible }
                                    : col
                                );
                                console.log('Updated columnConfig:', updated);
                                // Save to PocketBase
                                saveColumnPreferences(updated);
                                return updated;
                              });
                            };

                            return (
                              <div 
                                key={column.key} 
                                className={`flex items-center p-2 rounded hover:bg-gray-50 ${
                                  column.required ? 'opacity-60' : 'cursor-pointer'
                                }`}
                                onClick={handleToggle}
                              >
                                <input
                                  type="checkbox"
                                  checked={column.visible}
                                  disabled={column.required}
                                  onChange={() => {}} // Controlled by parent click
                                  className="mr-3 text-blue-600 focus:ring-blue-500 disabled:opacity-50 pointer-events-none"
                                />
                                <span className={`text-sm ${column.required ? 'text-gray-500' : 'text-gray-700'}`}>
                                  {column.label}
                                  {column.required && (
                                    <span className="ml-1 text-xs text-gray-400">(required)</span>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setShowCreatePartnerModal(true)}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 flex items-center"
                >
                  <Plus size={20} className="mr-2" />
                  New Partner
                </button>
              </div>
            </div>

            {/* Partners Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {columnConfig
                        .filter(col => col.visible)
                        .sort((a, b) => a.order - b.order)
                        .map(column => (
                          <th key={column.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {column.label}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPartners.map((partner) => (
                      <tr key={partner.id} className="hover:bg-gray-50">
                        {columnConfig
                          .filter(col => col.visible)
                          .sort((a, b) => a.order - b.order)
                          .map(column => (
                            <React.Fragment key={column.key}>
                              {renderTableCell(partner, column.key)}
                            </React.Fragment>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Partner Admins Tab */}
        {activeTab === 'admins' && (
          <div className="space-y-6">
            {/* Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search admins..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={() => setShowCreateAdminModal(true)}
                className="bg-gradient-to-r from-green-500 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-blue-700 transition duration-200 flex items-center"
              >
                <UserPlus size={20} className="mr-2" />
                New Partner Admin
              </button>
            </div>

            {/* Admins Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Admin
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Partner Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        First Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAdmins.map((admin) => (
                      <tr key={admin.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="bg-green-100 p-2 rounded-lg mr-3">
                              <Users className="text-green-600" size={16} />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                              <div className="text-sm text-gray-500">{admin.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {getPartnerName(admin.partner_id)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(admin.status)}`}>
                            {admin.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            admin.first_login_completed 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {admin.first_login_completed ? 'Completed' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {admin.last_login 
                            ? new Date(admin.last_login).toLocaleString('en-GB', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                              })
                            : 'Never'
                          }
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {admin.created_at 
                            ? new Date(admin.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : 'N/A'
                          }
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => startEditAdmin(admin)}
                              className="text-blue-600 hover:text-blue-800 transition duration-200"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete ${admin.name}?`)) {
                                  try {
                                    await deletePartnerAdmin(admin.id);
                                  } catch (error) {
                                    alert('Failed to delete partner admin: ' + error);
                                  }
                                }
                              }}
                              className="text-red-600 hover:text-red-800 transition duration-200"
                              title="Delete partner admin"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Marketing Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <MarketingCampaignManager />
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col gap-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Partner</label>
                    <select
                      value={billingSelectedPartner}
                      onChange={(e) => setBillingSelectedPartner(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Partners</option>
                      {partners.map((partner) => (
                        <option key={partner.id} value={partner.id}>
                          {partner.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={billingStartDate}
                      onChange={(e) => {
                        setBillingPreset('custom');
                        setBillingStartDate(e.target.value);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={billingEndDate}
                      onChange={(e) => {
                        setBillingPreset('custom');
                        setBillingEndDate(e.target.value);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'last_7_days' as BillingReportPreset, label: 'Last 7 days' },
                      { id: 'last_30_days' as BillingReportPreset, label: 'Last 30 days' },
                      { id: 'this_month' as BillingReportPreset, label: 'This month' },
                      { id: 'previous_month' as BillingReportPreset, label: 'Previous month' },
                      { id: 'custom' as BillingReportPreset, label: 'Custom' }
                    ].map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => handlePresetChange(preset.id)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                          billingPreset === preset.id
                            ? 'bg-blue-50 border-blue-200 text-blue-600'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={loadBillingReport}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Refresh Report
                    </button>
                    <button
                      type="button"
                      onClick={handleExportPdf}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                      disabled={!billingResult || billingLoading}
                    >
                      Export to PDF
                    </button>
                    {billingLoading && (
                      <span className="text-sm text-gray-500">Loading...</span>
                    )}
                  </div>
                </div>
              </div>
              {billingError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {billingError}
                </div>
              )}
            </div>

            {!billingLoading && billingResult && (
              <div ref={billingReportRef} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <p className="text-sm text-gray-500">Magic Link Clicks</p>
                    <p className="text-3xl font-semibold text-gray-900 mt-2">
                      {billingResult.totals.magicLinkClicks.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <p className="text-sm text-gray-500">Promo Requests</p>
                    <p className="text-3xl font-semibold text-gray-900 mt-2">
                      {billingResult.totals.promoRequests.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <p className="text-sm text-gray-500">Allocated Ads</p>
                    <div className="mt-3 space-y-1">
                      {(['platinum','gold','silver','bronze'] as const).map((priority) => (
                        <div key={priority} className="flex items-center justify-between text-sm text-gray-600">
                          <span className="capitalize">{priority}</span>
                          <span className="font-semibold text-gray-900">
                            {billingResult.totals.adCounts[priority].toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Partner Breakdown</h3>
                      <p className="text-sm text-gray-500">
                        Showing {billingResult.perPartner.length} partner{billingResult.perPartner.length === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                            onClick={() => handleBillingSort('partner')}
                          >
                            Partner
                          </th>
                          {(['platinum','gold','silver','bronze'] as const).map((priority) => (
                            <th
                              key={priority}
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                              onClick={() => handleBillingSort(priority)}
                            >
                              {priority}
                            </th>
                          ))}
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                            onClick={() => handleBillingSort('magicLinkClicks')}
                          >
                            Magic Link Clicks
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                            onClick={() => handleBillingSort('promoRequests')}
                          >
                            Promo Requests
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                            onClick={() => handleBillingSort('ranking')}
                          >
                            Ranking
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {billingResult.perPartner.length === 0 && (
                          <tr>
                            <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">
                              No activity found for this period.
                            </td>
                          </tr>
                        )}
                        {(() => {
                          // Compute ranking based on magic link clicks for this period
                          // Ranking is calculated against ALL partners in the period,
                          // independent of the current Partner filter.
                          const rows = billingResult.perPartner;
                          if (rows.length === 0) return null;

                          const allRows = billingResultAllPartners?.perPartner && billingResultAllPartners.perPartner.length > 0
                            ? billingResultAllPartners.perPartner
                            : rows;

                          // Active partners are those with > 0 magic link clicks
                          const activeRows = allRows.filter((r) => r.magicLinkClicks > 0);
                          const rankingBase = [...activeRows].sort((a, b) => b.magicLinkClicks - a.magicLinkClicks);
                          const totalActive = rankingBase.length;

                          const getTierForPartner = (partnerId: string, magicLinkClicks: number): 'A' | 'B' | 'C' | 'D' => {
                            // All inactive partners (0 clicks) are always D
                            if (!magicLinkClicks || magicLinkClicks <= 0) {
                              return 'D';
                            }

                            if (totalActive === 0) {
                              return 'D';
                            }

                            const index = rankingBase.findIndex((r) => r.partnerId === partnerId);
                            if (index === -1) return 'D';

                            const percentile = index / totalActive;
                            if (percentile < 0.10) return 'A'; // Top 10%
                            if (percentile < 0.30) return 'B'; // Next 20%
                            if (percentile < 0.60) return 'C'; // Next 30%
                            return 'D'; // Remaining active partners
                          };

                          const tierOrder: Record<'A' | 'B' | 'C' | 'D', number> = { A: 0, B: 1, C: 2, D: 3 };

                          const sortedRows = [...rows].sort((a, b) => {
                            const partnerA = partners.find((p) => p.id === a.partnerId);
                            const partnerB = partners.find((p) => p.id === b.partnerId);
                            const nameA = (partnerA?.name || 'Unknown Partner').toLowerCase();
                            const nameB = (partnerB?.name || 'Unknown Partner').toLowerCase();

                            const tierA = getTierForPartner(a.partnerId, a.magicLinkClicks);
                            const tierB = getTierForPartner(b.partnerId, b.magicLinkClicks);

                            let cmp = 0;

                            switch (billingSortColumn) {
                              case 'partner':
                                cmp = nameA.localeCompare(nameB);
                                break;
                              case 'platinum':
                              case 'gold':
                              case 'silver':
                              case 'bronze':
                                cmp = a.adCounts[billingSortColumn] - b.adCounts[billingSortColumn];
                                break;
                              case 'magicLinkClicks':
                                cmp = a.magicLinkClicks - b.magicLinkClicks;
                                break;
                              case 'promoRequests':
                                cmp = a.promoRequests - b.promoRequests;
                                break;
                              case 'ranking':
                                cmp = tierOrder[tierA] - tierOrder[tierB];
                                break;
                              default:
                                cmp = 0;
                            }

                            if (billingSortDirection === 'desc') {
                              cmp = -cmp;
                            }

                            // Stable secondary sort by partner name for deterministic order
                            if (cmp === 0) {
                              return nameA.localeCompare(nameB);
                            }
                            return cmp;
                          });

                          return sortedRows.map((row) => {
                            const partner = partners.find((p) => p.id === row.partnerId);
                            const tier = getTierForPartner(row.partnerId, row.magicLinkClicks);
                            return (
                              <tr key={row.partnerId}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {partner?.name || 'Unknown Partner'}
                                </td>
                                {(['platinum','gold','silver','bronze'] as const).map((priority) => (
                                  <td key={priority} className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                    {row.adCounts[priority].toLocaleString()}
                                  </td>
                                ))}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {row.magicLinkClicks.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {row.promoRequests.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                  {tier}
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Filters Subsection */}
            {activeSettingsTab === 'filters' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Filter Management</h2>
                    <p className="text-gray-600 mt-1 text-sm">
                      Configure the filters used across public views and advertisement creation.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setEditingFilter(null);
                      resetFilterForm();
                      setShowFilterModal(true);
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition duration-200 flex items-center"
                  >
                    <Plus size={20} className="mr-2" />
                    Add Filter
                  </button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {filters.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">
                      No filters defined yet. Use "Add Filter" to create your first filter.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filters.map(filter => (
                            <tr key={filter.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{filter.name}</div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {filter.description || '—'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  filter.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {filter.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => {
                                      setEditingFilter(filter);
                                      setFilterForm({
                                        name: filter.name,
                                        description: filter.description || '',
                                        status: filter.status
                                      });
                                      setShowFilterModal(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition duration-200"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFilter(filter)}
                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition duration-200"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {billingLoading && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center text-gray-500">
                Loading billing data...
              </div>
            )}

            {!billingLoading && !billingResult && !billingError && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center text-gray-500">
                Generate a report to view billing metrics.
              </div>
            )}
          </div>
        )}

        {/* Advertisement Approvals Tab */}
        {activeTab === 'approvals' && (
          <GlobalAdminApprovals />
        )}

        {/* Promo Requests Tab */}
        {activeTab === 'promo-requests' && (
          <PromoRequestsManager />
        )}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <SubscribersManager />
        )}

        {/* Contact Inquiries Tab */}
        {activeTab === 'contact-inquiries' && (
          <ContactInquiriesManager />
        )}

        {/* Create/Edit Partner Modal */}
        {(showCreatePartnerModal || editingPartner) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingPartner ? 'Edit Partner' : 'Create New Partner'}
              </h3>
              
              {/* Debug info - remove in production */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mb-4 p-2 bg-gray-100 rounded text-xs">
                  <strong>Debug:</strong> Integration Settings: {JSON.stringify(partnerForm.integration_settings)}
                </div>
              )}
              
              {/* Error display */}
              {partnerError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{partnerError}</p>
                </div>
              )}
              
              <form onSubmit={editingPartner ? handleUpdatePartner : handleCreatePartner} className="space-y-6">
                {/* Basic Information Section */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Partner Name</label>
                      <input
                        type="text"
                        value={partnerForm.name}
                        onChange={(e) => setPartnerForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter partner name"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select
                        value={partnerForm.category_id}
                        onChange={(e) => {
                          const selectedCategory = categories.find(cat => cat.id === e.target.value);
                          setPartnerForm(prev => ({ 
                            ...prev, 
                            category_id: e.target.value,
                            category: selectedCategory?.name || ''
                          }));
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Select a category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Campaigns (1-100)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={partnerForm.max_campaigns}
                        onChange={(e) => setPartnerForm(prev => ({ ...prev, max_campaigns: parseInt(e.target.value) }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={partnerForm.status}
                        onChange={(e) => setPartnerForm(prev => ({ ...prev, status: e.target.value as Partner['status'] }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Partner Logo</label>
                      
                      {/* Logo URL input - First */}
                      <div className="mb-4">
                        <input
                          type="url"
                          value={partnerForm.logo_url}
                          onChange={(e) => {
                            setPartnerForm(prev => ({ ...prev, logo_url: e.target.value }));
                            // Clear file upload when URL is entered
                            if (e.target.value) {
                              setLogoFile(null);
                              setLogoPreview('');
                            }
                          }}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter logo URL (e.g., https://example.com/logo.png)"
                          disabled={!!logoFile}
                        />
                        {logoFile && (
                          <p className="text-xs text-gray-500 mt-1">
                            Clear uploaded file to use URL instead
                          </p>
                        )}
                      </div>

                      {/* OR divider */}
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">OR upload image</span>
                        </div>
                      </div>

                      {/* Two column layout: Drop zone left, Preview right */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Left: Drop Zone */}
                        <div
                          className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                            logoDragActive 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                          onDragEnter={handleLogoDrag}
                          onDragLeave={handleLogoDrag}
                          onDragOver={handleLogoDrag}
                          onDrop={handleLogoDrop}
                        >
                          <div className="text-center">
                            <div className="mx-auto w-12 h-12 text-gray-400 mb-3">
                              <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">
                              <label htmlFor="logo-upload" className="text-blue-600 hover:text-blue-700 cursor-pointer font-medium">
                                Click to upload
                              </label>
                              {' '}or drag and drop
                            </p>
                            <p className="text-xs text-gray-500 mb-2">
                              PNG, JPG, WebP or SVG
                            </p>
                            <p className="text-xs text-gray-400">
                              Max size: 2MB
                            </p>
                            <p className="text-xs text-gray-400">
                              Recommended: 200x200px, square
                            </p>
                            <input
                              id="logo-upload"
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml"
                              onChange={(e) => handleLogoFileChange(e.target.files?.[0] || null)}
                              className="hidden"
                            />
                          </div>
                        </div>

                        {/* Right: Preview */}
                        <div className="border-2 border-gray-200 rounded-lg p-6 bg-gray-50 flex items-center justify-center">
                          {logoPreview || partnerForm.logo_url ? (
                            <div className="text-center">
                              <img 
                                src={logoPreview || partnerForm.logo_url} 
                                alt="Logo preview" 
                                className="w-32 h-32 object-contain mx-auto rounded-lg border border-gray-300 bg-white p-2"
                              />
                              <p className="text-sm font-medium text-gray-900 mt-3">Preview</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {logoFile ? logoFile.name : 'From URL'}
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  setLogoFile(null);
                                  setLogoPreview('');
                                  setPartnerForm(prev => ({ ...prev, logo_url: '' }));
                                }}
                                className="mt-3 text-sm text-red-600 hover:text-red-700 font-medium"
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <div className="text-center text-gray-400">
                              <div className="mx-auto w-16 h-16 mb-2">
                                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </div>
                              <p className="text-sm">Preview</p>
                              <p className="text-xs mt-1">No image selected</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Web Domain</label>
                      <input
                        type="text"
                        value={partnerForm.web_domain}
                        onChange={(e) => setPartnerForm(prev => ({ ...prev, web_domain: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="example.com"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Policy URL</label>
                      <input
                        type="url"
                        value={partnerForm.privacy_policy_url}
                        onChange={(e) => setPartnerForm(prev => ({ ...prev, privacy_policy_url: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="https://example.com/privacy-policy"
                      />
                    </div>
                  </div>
                  
                  {/* Region - Full Width */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Region</label>
                    <MultiSelectCountries
                      selectedCountries={partnerForm.region}
                      onChange={(countries) => setPartnerForm(prev => ({ ...prev, region: countries }))}
                      placeholder="Select countries..."
                      className="w-full"
                    />
                  </div>
                </div>
                
                {/* Priority Quotas Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Advertisement Priority Quotas</h4>
                  <p className="text-sm text-gray-600 mb-4">Set the maximum number of advertisements allowed for each priority level</p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="inline-flex items-center">
                          <span className="w-3 h-3 rounded-full bg-purple-600 mr-2"></span>
                          Platinum
                        </span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={partnerForm.priority_quotas.platinum}
                        onChange={(e) => setPartnerForm(prev => ({
                          ...prev,
                          priority_quotas: {
                            ...prev.priority_quotas,
                            platinum: parseInt(e.target.value) || 0
                          }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="inline-flex items-center">
                          <span className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></span>
                          Gold
                        </span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={partnerForm.priority_quotas.gold}
                        onChange={(e) => setPartnerForm(prev => ({
                          ...prev,
                          priority_quotas: {
                            ...prev.priority_quotas,
                            gold: parseInt(e.target.value) || 0
                          }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="inline-flex items-center">
                          <span className="w-3 h-3 rounded-full bg-gray-400 mr-2"></span>
                          Silver
                        </span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={partnerForm.priority_quotas.silver}
                        onChange={(e) => setPartnerForm(prev => ({
                          ...prev,
                          priority_quotas: {
                            ...prev.priority_quotas,
                            silver: parseInt(e.target.value) || 0
                          }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <span className="inline-flex items-center">
                          <span className="w-3 h-3 rounded-full bg-orange-600 mr-2"></span>
                          Bronze
                        </span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={partnerForm.priority_quotas.bronze}
                        onChange={(e) => setPartnerForm(prev => ({
                          ...prev,
                          priority_quotas: {
                            ...prev.priority_quotas,
                            bronze: parseInt(e.target.value) || 0
                          }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Integration Settings Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Integration Permissions</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div 
                      className="flex items-start p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        console.log('Email notifications card clicked');
                        setPartnerForm(prev => ({
                          ...prev,
                          integration_settings: {
                            ...prev.integration_settings,
                            email_notifications: !prev.integration_settings.email_notifications
                          }
                        }));
                      }}
                    >
                      <input
                        type="checkbox"
                        id="email_notifications"
                        checked={partnerForm.integration_settings.email_notifications}
                        onChange={(e) => {
                          console.log('Email notifications checkbox changed:', e.target.checked);
                          e.stopPropagation();
                          setPartnerForm(prev => ({
                            ...prev,
                            integration_settings: {
                              ...prev.integration_settings,
                              email_notifications: e.target.checked
                            }
                          }));
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1 pointer-events-none"
                      />
                      <div className="ml-3">
                        <label htmlFor="email_notifications" className="text-sm font-medium text-gray-700 pointer-events-none">
                          Email Notifications
                        </label>
                        <p className="text-xs text-gray-500 mt-1 pointer-events-none">Allow partner to configure email notification settings</p>
                      </div>
                    </div>
                    
                    <div 
                      className="flex items-start p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        console.log('API access card clicked');
                        setPartnerForm(prev => ({
                          ...prev,
                          integration_settings: {
                            ...prev.integration_settings,
                            api_access: !prev.integration_settings.api_access
                          }
                        }));
                      }}
                    >
                      <input
                        type="checkbox"
                        id="api_access"
                        checked={partnerForm.integration_settings.api_access}
                        onChange={(e) => {
                          console.log('API access checkbox changed:', e.target.checked);
                          e.stopPropagation();
                          setPartnerForm(prev => ({
                            ...prev,
                            integration_settings: {
                              ...prev.integration_settings,
                              api_access: e.target.checked
                            }
                          }));
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1 pointer-events-none"
                      />
                      <div className="ml-3">
                        <label htmlFor="api_access" className="text-sm font-medium text-gray-700 pointer-events-none">
                          API Access
                        </label>
                        <p className="text-xs text-gray-500 mt-1 pointer-events-none">Allow partner to configure API integrations and access tokens</p>
                      </div>
                    </div>
                    
                    <div 
                      className="flex items-start p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        console.log('Webhook access card clicked');
                        setPartnerForm(prev => ({
                          ...prev,
                          integration_settings: {
                            ...prev.integration_settings,
                            webhook_access: !prev.integration_settings.webhook_access
                          }
                        }));
                      }}
                    >
                      <input
                        type="checkbox"
                        id="webhook_access"
                        checked={partnerForm.integration_settings.webhook_access}
                        onChange={(e) => {
                          console.log('Webhook access checkbox changed:', e.target.checked);
                          e.stopPropagation();
                          setPartnerForm(prev => ({
                            ...prev,
                            integration_settings: {
                              ...prev.integration_settings,
                              webhook_access: e.target.checked
                            }
                          }));
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1 pointer-events-none"
                      />
                      <div className="ml-3">
                        <label htmlFor="webhook_access" className="text-sm font-medium text-gray-700 pointer-events-none">
                          Webhook Access
                        </label>
                        <p className="text-xs text-gray-500 mt-1 pointer-events-none">Allow partner to configure webhook endpoints and callbacks</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreatePartnerModal(false);
                      setEditingPartner(null);
                      resetPartnerForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200"
                  >
                    {editingPartner ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Create/Edit Partner Admin Modal */}
        {(showCreateAdminModal || editingAdmin) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingAdmin ? 'Edit Partner Admin' : 'Create New Partner Admin'}
              </h3>
              <form onSubmit={editingAdmin ? handleUpdateAdmin : handleCreateAdmin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    value={adminForm.name}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter admin name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    value={adminForm.email}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email address"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Partner Company</label>
                  <select
                    value={adminForm.partner_id}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, partner_id: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a partner company</option>
                    {partners.map(partner => (
                      <option key={partner.id} value={partner.id}>
                        {partner.name} ({partner.category})
                      </option>
                    ))}
                  </select>
                </div>
                {!editingAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Activation Method
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="activation_method"
                          value="password"
                          checked={adminForm.activation_method === 'password'}
                          onChange={(e) => setAdminForm(prev => ({ ...prev, activation_method: e.target.value as 'password' | 'email_link' }))}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Initial Password</div>
                          <div className="text-sm text-gray-500">Assign a temporary password that must be changed on first login</div>
                        </div>
                      </label>
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="activation_method"
                          value="email_link"
                          checked={adminForm.activation_method === 'email_link'}
                          onChange={(e) => setAdminForm(prev => ({ ...prev, activation_method: e.target.value as 'password' | 'email_link' }))}
                          className="mt-1 mr-3"
                        />
                        <div>
                          <div className="font-medium text-gray-900">Secure Email Link</div>
                          <div className="text-sm text-gray-500">Send a one-time activation link via email (valid for 48 hours)</div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
                {adminForm.activation_method === 'password' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Initial Password
                    </label>
                    <input
                      type="password"
                      value={adminForm.initial_password}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, initial_password: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter initial password"
                      required={!editingAdmin && adminForm.activation_method === 'password'}
                      minLength={10}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Minimum 10 characters. Admin will be required to change this on first login.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={adminForm.status}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, status: e.target.value as PartnerAdmin['status'] }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                {!editingAdmin && adminForm.activation_method === 'password' && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Important:</strong> The partner admin will be required to change this initial password during their first login using their email address.
                    </p>
                  </div>
                )}
                {!editingAdmin && adminForm.activation_method === 'email_link' && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>Secure Activation:</strong> An email with a secure one-time activation link will be sent to the admin's email address. The link will be valid for 48 hours.
                    </p>
                  </div>
                )}
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateAdminModal(false);
                      setEditingAdmin(null);
                      resetAdminForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-lg hover:from-green-600 hover:to-blue-700 transition duration-200"
                  >
                    {editingAdmin ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Settings Sub-navigation */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-6">
                <button
                  onClick={() => setActiveSettingsTab('categories')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    activeSettingsTab === 'categories'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Categories
                </button>
                <button
                  onClick={() => setActiveSettingsTab('filters')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    activeSettingsTab === 'filters'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Filters
                </button>
                <button
                  onClick={() => setActiveSettingsTab('activity-logs')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    activeSettingsTab === 'activity-logs'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Activity Logs
                </button>
                <button
                  onClick={() => setActiveSettingsTab('debug')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    activeSettingsTab === 'debug'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Debug Settings
                </button>
                <button
                  onClick={() => setActiveSettingsTab('data-deletion')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    activeSettingsTab === 'data-deletion'
                      ? 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Data Deletion
                </button>
                <button
                  onClick={() => setActiveSettingsTab('send-email')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    activeSettingsTab === 'send-email'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Send Email
                </button>
                <button
                  onClick={() => setActiveSettingsTab('web-visitors')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm ${
                    activeSettingsTab === 'web-visitors'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Web Visitors
                </button>
              </nav>
            </div>

            {/* Categories Subsection */}
            {activeSettingsTab === 'categories' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Category Management</h2>
                    <p className="text-gray-600 mt-1 text-sm">
                      Configure the categories used to organize and filter advertisements.
                    </p>
                  </div>
                  <button
                onClick={() => setShowCreateCategoryModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-lg hover:from-green-600 hover:to-blue-700 transition duration-200 flex items-center"
              >
                <Plus size={20} className="mr-2" />
                Add Category
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {categories.map((category) => (
                      <tr key={category.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{category.name}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{category.description}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            category.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {category.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setEditingCategory(category);
                                setCategoryForm({
                                  name: category.name,
                                  description: category.description || '',
                                  status: category.status
                                });
                                setShowCreateCategoryModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition duration-200"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={async () => {
                                if (confirm(`Are you sure you want to delete the ${category.name} category?`)) {
                                  try {
                                    await deleteCategory(category.id);
                                  } catch (error) {
                                    console.error('Failed to delete category:', error);
                                    const errorMessage = error instanceof Error ? error.message : 'Failed to delete category. Please try again.';
                                    alert(errorMessage);
                                  }
                                }
                              }}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition duration-200"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
              </div>
            )}

            {/* Filters Subsection */}
            {activeSettingsTab === 'filters' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Filter Management</h2>
                    <p className="text-gray-600 mt-1 text-sm">
                      Configure the filters used across public views and advertisement creation.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setEditingFilter(null);
                      resetFilterForm();
                      setShowFilterModal(true);
                    }}
                    className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition duration-200 flex items-center"
                  >
                    <Plus size={20} className="mr-2" />
                    Add Filter
                  </button>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {filters.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">
                      No filters defined yet. Use "Add Filter" to create your first filter.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filters.map(filter => (
                            <tr key={filter.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{filter.name}</div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {filter.description || '—'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  filter.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {filter.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => {
                                      setEditingFilter(filter);
                                      setFilterForm({
                                        name: filter.name,
                                        description: filter.description || '',
                                        status: filter.status
                                      });
                                      setShowFilterModal(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition duration-200"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFilter(filter)}
                                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition duration-200"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Activity Logs Subsection */}
            {activeSettingsTab === 'activity-logs' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Activity Logs</h2>
                    <p className="text-gray-600 mt-1">
                      Monitor all system activity, authentication events, and partner actions.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                  <ActivityLogsManager userRole="global_admin" />
                </div>
              </div>
            )}

            {/* Debug Settings Subsection */}
            {activeSettingsTab === 'debug' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Debug Settings</h2>
                    <p className="text-gray-600 mt-1">
                      Control debug logging for the entire platform or specific partners.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  {debugLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  ) : debugConfig ? (
                    <div className="space-y-6">
                      {/* Global Debug Toggle */}
                      <div className="border-b border-gray-200 pb-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Global Debug Mode</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Enable or disable debug logging for the entire platform. When disabled, console logs will be suppressed in production.
                            </p>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const newValue = !debugConfig.global_debug_enabled;
                                // Optimistic update
                                setDebugConfig({ ...debugConfig, global_debug_enabled: newValue });
                                // Save to backend
                                await debugConfigService.setGlobalDebug(newValue);
                              } catch (error) {
                                console.error('Failed to toggle global debug:', error);
                                alert('Failed to update debug settings');
                                // Revert on error
                                const current = await debugConfigService.getConfig();
                                setDebugConfig(current);
                              }
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              debugConfig.global_debug_enabled ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                debugConfig.global_debug_enabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm text-yellow-800">
                            <strong>⚠️ Note:</strong> Disabling debug mode will reduce console output in production but may make troubleshooting more difficult.
                          </p>
                        </div>
                      </div>

                      {/* reCAPTCHA Toggle */}
                      <div className="border-b border-gray-200 pb-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">reCAPTCHA Verification</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Enable or disable reCAPTCHA verification for contact and promocode forms. Disable for development/testing.
                            </p>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                const newValue = !debugConfig.recaptcha_enabled;
                                // Optimistic update
                                setDebugConfig({ ...debugConfig, recaptcha_enabled: newValue });
                                // Save to backend
                                await debugConfigService.setRecaptchaEnabled(newValue);
                              } catch (error) {
                                console.error('Failed to toggle reCAPTCHA:', error);
                                alert('Failed to update reCAPTCHA settings');
                                // Revert on error
                                const current = await debugConfigService.getConfig();
                                setDebugConfig(current);
                              }
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              debugConfig.recaptcha_enabled ? 'bg-green-600' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                debugConfig.recaptcha_enabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>ℹ️ Info:</strong> When disabled, forms will submit without reCAPTCHA verification. Enable in production to protect against spam and bots.
                          </p>
                        </div>
                      </div>

                      {/* Partner-Specific Overrides */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Partner-Specific Overrides</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              Override debug settings for specific partners. These settings take precedence over the global setting.
                            </p>
                          </div>
                          {Object.keys(debugConfig.partner_debug_overrides).length > 0 && (
                            <button
                              onClick={async () => {
                                if (confirm('Clear all partner-specific debug overrides?')) {
                                  try {
                                    // Optimistic update
                                    setDebugConfig({ ...debugConfig, partner_debug_overrides: {} });
                                    // Save to backend
                                    await debugConfigService.clearAllPartnerOverrides();
                                  } catch (error) {
                                    console.error('Failed to clear overrides:', error);
                                    alert('Failed to clear overrides');
                                    // Revert on error
                                    const current = await debugConfigService.getConfig();
                                    setDebugConfig(current);
                                  }
                                }
                              }}
                              className="px-4 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                            >
                              Clear All Overrides
                            </button>
                          )}
                        </div>

                        {/* List of partners with overrides */}
                        {Object.keys(debugConfig.partner_debug_overrides).length > 0 ? (
                          <div className="space-y-2">
                            {Object.entries(debugConfig.partner_debug_overrides).map(([partnerId, enabled]) => {
                              const partner = partners.find(p => p.id === partnerId);
                              return (
                                <div key={partnerId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                  <div>
                                    <p className="font-medium text-gray-900">{partner?.name || partnerId}</p>
                                    <p className="text-sm text-gray-600">Debug: {enabled ? 'Enabled' : 'Disabled'}</p>
                                  </div>
                                  <button
                                    onClick={async () => {
                                      try {
                                        // Optimistic update
                                        const newOverrides = { ...debugConfig.partner_debug_overrides };
                                        delete newOverrides[partnerId];
                                        setDebugConfig({ ...debugConfig, partner_debug_overrides: newOverrides });
                                        // Save to backend
                                        await debugConfigService.setPartnerDebug(partnerId, null);
                                      } catch (error) {
                                        console.error('Failed to remove override:', error);
                                        alert('Failed to remove override');
                                        // Revert on error
                                        const current = await debugConfigService.getConfig();
                                        setDebugConfig(current);
                                      }
                                    }}
                                    className="px-3 py-1 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition"
                                  >
                                    Remove Override
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 text-sm">No partner-specific overrides configured</p>
                            <p className="text-gray-400 text-xs mt-1">All partners use the global debug setting</p>
                          </div>
                        )}

                        {/* Add Partner Override */}
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-800 mb-3">
                            <strong>Add Partner Override:</strong> Select a partner and choose whether to enable or disable debug logging for them.
                          </p>
                          <div className="flex gap-2">
                            <select
                              id="partner-select"
                              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select a partner...</option>
                              {partners.map(partner => (
                                <option key={partner.id} value={partner.id}>{partner.name}</option>
                              ))}
                            </select>
                            <button
                              onClick={async () => {
                                const select = document.getElementById('partner-select') as HTMLSelectElement;
                                const partnerId = select.value;
                                if (!partnerId) {
                                  alert('Please select a partner');
                                  return;
                                }
                                try {
                                  // Optimistic update
                                  setDebugConfig({
                                    ...debugConfig,
                                    partner_debug_overrides: { ...debugConfig.partner_debug_overrides, [partnerId]: true }
                                  });
                                  select.value = '';
                                  // Save to backend
                                  await debugConfigService.setPartnerDebug(partnerId, true);
                                } catch (error) {
                                  console.error('Failed to add override:', error);
                                  alert('Failed to add override');
                                  // Revert on error
                                  const current = await debugConfigService.getConfig();
                                  setDebugConfig(current);
                                }
                              }}
                              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                            >
                              Enable
                            </button>
                            <button
                              onClick={async () => {
                                const select = document.getElementById('partner-select') as HTMLSelectElement;
                                const partnerId = select.value;
                                if (!partnerId) {
                                  alert('Please select a partner');
                                  return;
                                }
                                try {
                                  // Optimistic update
                                  setDebugConfig({
                                    ...debugConfig,
                                    partner_debug_overrides: { ...debugConfig.partner_debug_overrides, [partnerId]: false }
                                  });
                                  select.value = '';
                                  // Save to backend
                                  await debugConfigService.setPartnerDebug(partnerId, false);
                                } catch (error) {
                                  console.error('Failed to add override:', error);
                                  alert('Failed to add override');
                                  // Revert on error
                                  const current = await debugConfigService.getConfig();
                                  setDebugConfig(current);
                                }
                              }}
                              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                            >
                              Disable
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500">Failed to load debug configuration</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Data Deletion Subsection */}
            {activeSettingsTab === 'data-deletion' && (
              <DataDeletionManager partners={partners} />
            )}

            {/* Send Email Subsection */}
            {activeSettingsTab === 'send-email' && (
              <AdminEmailManager />
            )}

            {activeSettingsTab === 'web-visitors' && (
              <WebVisitorsDashboard />
            )}
          </div>
        )}

        {/* Create/Edit Filter Modal */}
        {showFilterModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingFilter ? 'Edit Filter' : 'Create New Filter'}
              </h3>
              <form onSubmit={handleSaveFilter} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter Name</label>
                  <input
                    type="text"
                    value={filterForm.name}
                    onChange={(e) => setFilterForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter filter name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={filterForm.description}
                    onChange={(e) => setFilterForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Short description"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={filterForm.status}
                    onChange={(e) => setFilterForm(prev => ({ ...prev, status: e.target.value as FilterStatus }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFilterModal(false);
                      setEditingFilter(null);
                      resetFilterForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 transition duration-200"
                  >
                    {editingFilter ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Create/Edit Category Modal */}
        {showCreateCategoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingCategory ? 'Edit Category' : 'Create New Category'}
              </h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (editingCategory) {
                    await updateCategory(editingCategory.id, {
                      name: categoryForm.name,
                      description: categoryForm.description,
                      status: categoryForm.status
                    });
                  } else {
                    await createCategory({
                      name: categoryForm.name,
                      description: categoryForm.description,
                      status: categoryForm.status
                    });
                  }
                  setShowCreateCategoryModal(false);
                  setEditingCategory(null);
                  setCategoryForm({ name: '', description: '', status: 'active' });
                } catch (error) {
                  console.error('Failed to save category:', error);
                  const errorMessage = error instanceof Error ? error.message : 'Failed to save category. Please try again.';
                  alert(errorMessage);
                }
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category Name</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter category name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter category description"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={categoryForm.status}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateCategoryModal(false);
                      setEditingCategory(null);
                      setCategoryForm({ name: '', description: '', status: 'active' });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-lg hover:from-green-600 hover:to-blue-700 transition duration-200"
                  >
                    {editingCategory ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};